<template>
  <v-dialog
    :value="value"
    max-width="440"
    persistent
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="azul-run-title">{{ titulo }}</v-card-title>
      <v-card-text>
        <div v-if="targets.length > 1" class="azul-run-bases">
          <span v-for="t in targets" :key="t.slug" class="azul-run-base">{{
            t.label
          }}</span>
        </div>

        <template v-if="!resultados.length">
          <v-date-picker
            v-model="range"
            range
            no-title
            locale="pt-br"
            color="#0f7a6e"
            width="100%"
          />
          <p v-if="range.length === 2" class="azul-run-period">
            {{ periodoTexto }} · {{ dias }} dia(s)
          </p>
          <p v-if="excede" class="azul-run-error">
            <v-icon size="14" color="#c81e22">mdi-alert-circle-outline</v-icon>
            Período de {{ dias }} dias excede o máximo de {{ maxDias }} dias por
            execução.
          </p>
        </template>

        <ul v-else class="azul-run-results">
          <li
            v-for="r in resultados"
            :key="r.slug"
            :class="r.ok ? 'is-ok' : 'is-erro'"
          >
            <v-icon size="16" :color="r.ok ? '#1e8a4c' : '#c81e22'">{{
              r.ok ? "mdi-check-circle" : "mdi-alert-circle"
            }}</v-icon>
            <strong>{{ r.label }}</strong>
            <span>{{ r.ok ? "pedido enviado" : r.msg }}</span>
          </li>
        </ul>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text :disabled="enviando" @click="fechar">{{
          resultados.length ? "Fechar" : "Cancelar"
        }}</v-btn>
        <v-btn
          v-if="!resultados.length"
          color="#0f7a6e"
          dark
          depressed
          :loading="enviando"
          :disabled="range.length < 2 || excede"
          @click="executar"
        >
          Executar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { runAzul, azulErro } from "@/services/azulApi.js";
import { isoParaBr } from "../azulStatus.js";

// Mesmo limite validado no servidor (AZUL_MAX_DIAS, padrao 30).
const MAX_DIAS = 30;

export default {
  name: "AzulRunDialog",
  props: {
    value: { type: Boolean, default: false },
    // [{ slug, label }]
    targets: { type: Array, default: () => [] },
  },
  data() {
    return {
      range: [],
      enviando: false,
      resultados: [],
      maxDias: MAX_DIAS,
    };
  },
  computed: {
    titulo() {
      // Mesmo texto do dialogo da CVC; com varias bases, o numero aparece no titulo.
      if (this.targets.length === 1) return "Qual período o agente deve rodar?";
      return `Qual período o agente deve rodar? (${this.targets.length} bases)`;
    },
    ordenado() {
      return [...this.range].sort();
    },
    dias() {
      if (this.range.length < 2) return 0;
      const [a, b] = this.ordenado;
      return (
        Math.round(
          (new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000
        ) + 1
      );
    },
    excede() {
      return this.range.length === 2 && this.dias > MAX_DIAS;
    },
    periodoTexto() {
      return `${isoParaBr(this.ordenado[0])} a ${isoParaBr(this.ordenado[1])}`;
    },
  },
  watch: {
    value(aberto) {
      if (!aberto) return;
      // Reabre sempre SEM dia selecionado: nao deixa um periodo de uma execucao
      // anterior (nem um padrao) vazar para a proxima sem o usuario escolher.
      this.range = [];
      this.resultados = [];
      this.enviando = false;
    },
  },
  methods: {
    fechar() {
      const houveEnvio = this.resultados.length > 0;
      this.$emit("input", false);
      if (houveEnvio) this.$emit("done", this.resultados);
    },
    async executar() {
      if (this.range.length < 2 || this.excede) return;
      const [a, b] = this.ordenado;
      this.enviando = true;
      const resultados = [];
      // Um pedido por base, em sequencia: o servidor recusa (409) a que ja estiver
      // rodando ou na fila, e essa recusa aparece so naquela base.
      for (const t of this.targets) {
        try {
          await runAzul(t.slug, {
            dataInicial: isoParaBr(a),
            dataFinal: isoParaBr(b),
          });
          resultados.push({ slug: t.slug, label: t.label, ok: true });
        } catch (err) {
          resultados.push({
            slug: t.slug,
            label: t.label,
            ok: false,
            msg: azulErro(err, "Falha ao solicitar"),
          });
        }
      }
      this.resultados = resultados;
      this.enviando = false;
      // Tudo certo: fecha na hora e segue para a tela normal (execucao e logs).
      // Se alguma base recusou, o resultado fica na tela para o usuario ver o motivo.
      if (resultados.every((r) => r.ok)) this.fechar();
    },
  },
};
</script>

<style scoped lang="scss">
.azul-run-title {
  font-size: 1.05rem;
  font-weight: 700;
}
.azul-run-bases {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}
.azul-run-base {
  font-size: 0.75rem;
  font-weight: 600;
  background: #eef6f4;
  color: #0b5c52;
  border-radius: 14px;
  padding: 3px 10px;
}
.azul-run-period {
  margin: 10px 0 0;
  font-size: 0.82rem;
  font-weight: 600;
  color: #26303a;
  text-align: center;
}
.azul-run-error {
  margin: 8px 0 0;
  font-size: 0.8rem;
  color: #c81e22;
}
.azul-run-results {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
    font-size: 0.85rem;
    border-bottom: 1px solid #f0f2f4;
  }
  li span {
    color: #6b737c;
  }
  li.is-erro span {
    color: #a3161a;
  }
}
</style>
